import { PeriodInDays } from "@/domain/filter/filterType";
import { dateUtils } from "@/utils/dateUtils";
import { translate } from "@/utils/translate";

/**
 * O período do filtro global (`useFilter`): o mesmo recorte vale no Dashboard,
 * nos Relatórios, no Resultado da cobrança, no Gestor de mensagens e nas
 * Campanhas. Escolher "Últimos 30 dias" numa tela e abrir a outra mostrando
 * "Hoje" era o que fazia o lojista comparar números de recortes diferentes.
 *
 * Nos atalhos só `periodInDays` vale e as datas ficam nulas; no personalizado
 * valem as duas datas.
 */
export type GlobalPeriod = {
  periodInDays: PeriodInDays;
  startAt: Date | null;
  endAt: Date | null;
};

/** Atalhos oferecidos em todo filtro de período global. */
export const PERIODOS_GLOBAIS = [
  PeriodInDays.zero,
  PeriodInDays.one,
  PeriodInDays.seven,
  PeriodInDays.thirty,
  PeriodInDays.ninety,
  PeriodInDays.custom,
];

/**
 * Hoje. É função, e não constante de módulo: uma aba deixada aberta atravessa
 * a virada do dia.
 */
export function periodoPadrao(): GlobalPeriod {
  return { periodInDays: PeriodInDays.zero, startAt: null, endAt: null };
}

/**
 * As datas que o período recorta, para as telas cuja API recebe intervalo e
 * não `periodInDays`. Calculadas na hora da consulta: "Hoje" guardado ontem
 * continua sendo hoje.
 */
/**
 * Datas do período, já resolvidas. `minDate` é o primeiro dia que a tela tem
 * dado: antes dele a consulta devolveria números incompletos, que o lojista
 * leria como se fossem reais. Vale tanto para o personalizado quanto para os
 * atalhos — "90 dias" de novembro alcança agosto, e seria o caminho silencioso
 * para furar o limite.
 */
export function datasDoPeriodo(
  periodo: GlobalPeriod,
  minDate?: Date
): {
  startAt: Date | null;
  endAt: Date | null;
} {
  const { startAt, endAt } = resolverDatas(periodo);

  return {
    startAt: minDate && startAt && startAt < minDate ? minDate : startAt,
    endAt,
  };
}

function resolverDatas(periodo: GlobalPeriod): {
  startAt: Date | null;
  endAt: Date | null;
} {
  // Meia-noite, e não o instante atual: as datas entram em chave de consulta,
  // e um `new Date()` diferente a cada render refaria a busca sem parar.
  const dia = (deslocamento: number) =>
    dateUtils.resetHmS(dateUtils.addDays(deslocamento));

  switch (periodo.periodInDays) {
    case PeriodInDays.zero:
      return { startAt: dia(0), endAt: dia(0) };
    case PeriodInDays.one:
      return { startAt: dia(-1), endAt: dia(-1) };
    case PeriodInDays.seven:
      return { startAt: dia(-7), endAt: dia(0) };
    case PeriodInDays.thirty:
      return { startAt: dia(-30), endAt: dia(0) };
    case PeriodInDays.ninety:
      return { startAt: dia(-90), endAt: dia(0) };
    default:
      return { startAt: periodo.startAt, endAt: periodo.endAt };
  }
}

/**
 * Só o personalizado guarda datas. Mandando as duas coisas, o serviço do
 * painel prefere o intervalo (`getDateRangeParams`) e o atalho deixaria de
 * valer.
 */
export function normalizarPeriodo(periodo: GlobalPeriod): GlobalPeriod {
  if (periodo.periodInDays === PeriodInDays.custom) return periodo;
  return { ...periodo, startAt: null, endAt: null };
}

/** O personalizado só recorta alguma coisa com as duas pontas. */
export function periodoIncompleto(periodo: GlobalPeriod): boolean {
  return (
    periodo.periodInDays === PeriodInDays.custom &&
    (!periodo.startAt || !periodo.endAt)
  );
}

/** Como o período aparece na etiqueta de filtro aplicado. */
export function periodoEscrito(periodo: GlobalPeriod): string {
  if (periodo.periodInDays !== PeriodInDays.custom) {
    return translate.period(periodo.periodInDays);
  }

  return periodo.startAt && periodo.endAt
    ? `${dateUtils.formatDateBR(periodo.startAt)} a ${dateUtils.formatDateBR(periodo.endAt)}`
    : "Sem data escolhida";
}

/**
 * Quantos dias (contando as duas pontas) o período recorta. Telas com limite
 * de intervalo na API (ex.: Resultado da cobrança, 92 dias) usam isto para
 * recusar o período antes de consultar — o global pode ter vindo de outra
 * tela, sem esse limite.
 */
export function diasDoPeriodo(periodo: GlobalPeriod): number | null {
  const { startAt, endAt } = datasDoPeriodo(periodo);
  if (!startAt || !endAt) return null;
  return (
    Math.round(
      (Date.UTC(endAt.getFullYear(), endAt.getMonth(), endAt.getDate()) -
        Date.UTC(
          startAt.getFullYear(),
          startAt.getMonth(),
          startAt.getDate()
        )) /
        86_400_000
    ) + 1
  );
}

/** O período passa do limite de dias da tela. Sem limite, nunca passa. */
export function periodoExcede(
  periodo: GlobalPeriod,
  maxDays?: number
): boolean {
  if (!maxDays) return false;
  const dias = diasDoPeriodo(periodo);
  return dias !== null && dias > maxDays;
}
